import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { SliceFlexSettings } from 'extensions/core/slice-flex/front/slice-flex-settings.model';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { Link } from 'shared/services/links.model';


@Component({
  selector: 'render-slice[slice]',
  templateUrl: 'render-slice.component.html',
  styleUrls: ['./render-slice.component.scss']
})
export class RenderSliceComponent extends UnsubscribeComponent implements OnInit {

  @Input() public slice!: IdtComponent<SliceFlexSettings>;

  public lang$!: Observable<string | null>;
  private mobileDevice: boolean = false;

  constructor(
    @Inject('Window') private readonly window: Window,
    private readonly renderSlugService: RenderSlugService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly productService: ProductsService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly renderStateService: RenderStateService
  ) {
    super();
  }

  @HostBinding('class.pointer-events')
  public get hasToAddPointerEvents(): boolean {
    return !!this.link;
  }

  @HostBinding('style.z-index')
  public get zIndex(): string | undefined {
    if (!this.hasPositionEnabled) {
      return undefined;
    }

    return this.mobileDevice
      ? this.slice?.settings?.general?.position?.zIndexMobile ?? 'auto'
      : this.slice?.settings?.general?.position?.zIndexDesktop ?? 'auto';
  }


  @HostBinding('class.section-position-relative')
  public get positionTypeRelative(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeMobile === 'relative';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeDesktop === 'relative';
  }

  @HostBinding('class.section-position-absolute')
  public get positionTypeAbsolute(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeMobile === 'absolute';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeDesktop === 'absolute';
  }

  @HostBinding('class.section-position-sticky')
  public get positionTypeSticky(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeMobile === 'sticky';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeDesktop === 'sticky';
  }

  @HostBinding('class.section-position-fixed')
  public get positionTypeFixed(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeMobile === 'fixed';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionTypeDesktop === 'fixed';
  }

  @HostBinding('class.section-position-top')
  public get positionTop(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationMobile === 'top';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationDesktop === 'top';
  }

  @HostBinding('class.section-position-bottom')
  public get positionBottom(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationMobile === 'bottom';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationDesktop === 'bottom';
  }

  @HostBinding('class.section-position-left')
  public get positionLeft(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationMobile === 'left';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationDesktop === 'left';
  }

  @HostBinding('class.section-position-right')
  public get positionRight(): boolean {
    if (this.mobileDevice) {
      return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationMobile === 'right';
    }
    return this.hasPositionEnabled && this.slice?.settings?.general?.position?.positionLocationDesktop === 'right';
  }

  public get link(): Link | null {
    const link = this.slice?.settings?.general?.link;
    return link?.type ? link : null;
  }

  public ngOnInit(): void {
    if (this.link !== null) {
      this.lang$ = this.renderStateService.getLanguage$(this.renderSlugService.getSlug());
    }

    this.breakpointObserver.observe(
      this.renderConfigService.config.breakpoints.map((breakpoint) => breakpoint.bp)
    ).pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe(() => {
      this.mobileDevice = this.renderByDeviceService.isMobile;
    });
  }

  public onRenderLinkClick(productInfo: { productId: string | undefined; categoryId: string | undefined }): void {
    if (productInfo.productId) {
      this.productService.productDetail(productInfo.productId, productInfo.categoryId).pipe(
        take(1)
      ).subscribe((product) => {
        const cmsEvent: CMSEvent<Product> = {
          name: 'productLink',
          type: 'click',
          data: product
        };
        this.cmsRenderEventsService.emitEvent(cmsEvent);
      });
    }
  }

  public onRenderComponent(): void {
    const event = new Event('loadProductLink');
    const linkData: RenderEvent = {
      event,
      extra: this.link,
      location: this.slice?.name ?? ''
    };
    this.renderEventsService.events.next(linkData);
  }

  private get hasPositionEnabled(): boolean {
    return !!this.slice?.settings?.general?.position?.isPositionEnabled;
  }

}
